import type { VideoApiActions } from '../util/apiActions';
import { abortableSleep } from '@mimir/common/async/abortableSleep';
import { ItemState } from '@mimir/common/dto/itemState';
import type { MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import type { PlayableItemGetOptions } from '@mimir/common/dto/playableItemDto';
import type { Logger } from '@mimir/common/types/logger';
import { ABORT_ERROR, isAbortError } from '@mimir/common/util/abortControl';
import { createUuid } from '@mimir/common/util/createUuid';
import { makeError } from '@mimir/common/util/exception';
import type { Fraction } from '@mimir/common/util/fractionType';
import { compareFractions } from '@mimir/common/util/integerMath';
import isEqual from 'lodash/isEqual';

/**
 * Delay past which we will stop polling if we have not obtained any new peaks.
 */
const MAX_PEAK_POLLING_DELAY_IN_MS = 30000;

const activeLivePollingThreads: Record<string, string> = {};

export async function liveItemMediaMonitoring(
  logger: Logger,
  apiActions: VideoApiActions,
  handler: (media: MediaAvailability, duration: Fraction) => void,
  abortSignal: AbortSignal,
  itemId: string,
  options: PlayableItemGetOptions,
  getPeakOffset: () => number | undefined,
  addPeaksDelta: (peakOffset: number | undefined, peaksDeltaArrays: string[] | null | undefined) => number,
) {
  const id = createUuid();
  try {
    activeLivePollingThreads[id] = itemId;
    logger.info(`Started monitoring live item: ${itemId}. Current on-going live polling threads: ${JSON.stringify(Object.values(activeLivePollingThreads))}`);
    let previousMedia : MediaAvailability | null = null;
    let previousDuration : Fraction = { numerator: 0, denominator: 1 };
    for await (const { media, duration } of getItemChanges(apiActions, itemId, abortSignal, options, getPeakOffset, addPeaksDelta)) {
      const mediaChanged = !isEqual(media, previousMedia);
      const durationOrZero = duration ?? { numerator: 0, denominator: 1 };
      const durationChanged = (compareFractions(durationOrZero, previousDuration) !== 0);
      if (mediaChanged || durationChanged) {
        handler(media, durationOrZero);
        previousMedia = media;
        previousDuration = durationOrZero;
      }
    }
  } finally {
    if (!activeLivePollingThreads[id]) {
      logger.warn(`Live polling thread with id ${id} not found`);
    } else {
      delete activeLivePollingThreads[id];
      logger.info(`Stopped monitoring live item: ${itemId}. Current on-going live polling threads: ${JSON.stringify(Object.values(activeLivePollingThreads))}`);
    }
  }
}

// ---- end of public API

async function* getItemChanges(
  api: VideoApiActions, itemId: string,
  abortSignal: AbortSignal,
  options: PlayableItemGetOptions,
  getPeakOffset: () => number | undefined,
  addPeaksDelta: (peakOffset: number | undefined, peaksDeltaArrays: string[] | null | undefined) => number,
): AsyncGenerator<{ media: MediaAvailability, duration: Fraction | null }> {
  let hasBeenStreaming = false;
  let lastPeaksSeen = Date.now();
  let peaksRequested = false;
  while (true) {
    try {
      const peakOffset = getPeakOffset();
      if (peakOffset !== undefined) {
        peaksRequested = true;
      }
      const result = await api.getPlayableItem(itemId, { ...options, peakOffset });
      if (result === null) {
        return;
      }
      const addedPeaks = addPeaksDelta(peakOffset, result.peaksDelta);
      if (addedPeaks > 0) {
        lastPeaksSeen = Date.now();
      }
      if (abortSignal.aborted) {
        throw makeError('Aborted', ABORT_ERROR);
      }

      if (!result.media) {
        return;
      }
      yield { media: result.media, duration: result.playableDuration ?? null };
      if (result.media?.streaming === true) {
        hasBeenStreaming = true;
      }

      // If we have obtained peaks recently, it means that there is still data to be obtained,
      // so we don't want to stop polling yet even if the item is reported as complete.
      // This may happen for instance with Kelda live folder ingests where the segments
      // are produced quickly and the item may be marked as complete before the backend
      // had time to process all the segments to create the peaks
      const recentPeaks = peaksRequested && (Date.now() - lastPeaksSeen) < MAX_PEAK_POLLING_DELAY_IN_MS;
      if (!recentPeaks) {
        if (result.media?.streaming === false && (hasBeenStreaming || ['resources_stopped', 'resources_deleted'].includes(result.liveRecordingInfo?.state ?? ''))) {
          // If we see a false after having seen a true or
          // the cloud live state is already 'resources_stopped' or 'resources_deleted',
          // then we know that we can stop polling
          return;
        }
        if (result.itemState === ItemState.COMPLETE) {
          return;
        }
      }
      await abortableSleep(2000, abortSignal);
    } catch (ex) {
      if (isAbortError(ex)) {
        break;
      }
      throw ex;
    }
  }
}

